import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Button, TextField, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider, Alert, CircularProgress, Container } from '@mui/material';
import { getApiUrl } from '../utils/apiConfig';
// Direct Firestore for now until Backend route is ready? No, let's stick to plan: API Call.
// Actually, for immediate feedback, let's try to read doc directly if possible, but rules likely block it. 
// Plan: Frontend calls Backend. Backend reads Firestore. 

const ResponderCotacao = () => {
    const { token } = useParams();
    const [quote, setQuote] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [observation, setObservation] = useState('');

    useEffect(() => {
        // Fetch Quote by Token
        const fetchQuote = async () => {
            try {
                // TODO: Replace with Real Backend Endpoint
                // fetch(`${getApiUrl()}/api/public/cotacao/${token}`)

                // For MVP/Demo WITHOUT Backend deployment yet (user is deploying):
                // We cannot easily do public reading without backend or open rules.
                // I will IMPLEMENT the backend route in next step.
                // Assuming backend runs on 5000.
                const res = await fetch(`${getApiUrl()}/api/cotacoes/public/${token}`);
                if (!res.ok) throw new Error("Cotação não encontrada ou link expirado.");

                const data = await res.json();
                if (data.status !== 'enviada' && data.status !== 'em_negociacao') {
                    throw new Error("Esta cotação não permite mais respostas.");
                }

                setQuote(data);
                setItems(data.itens || []);
                setObservation(data.observacoes_fornecedor || '');
            } catch (err) {
                console.error(err);
                setError(err.message || "Erro ao carregar cotação.");
            } finally {
                setLoading(false);
            }
        };
        fetchQuote();
    }, [token]);

    const handlePriceChange = (index, val) => {
        const newItems = [...items];
        newItems[index].preco_unitario = val;
        setItems(newItems);
    };

    const calculateTotal = () => {
        return items.reduce((acc, item) => acc + (parseFloat(item.quantidade) * (parseFloat(item.preco_unitario) || 0)), 0);
    };

    const handleSubmit = async () => {
        if (!window.confirm("Confirma o envio dos valores? A cotação entrará em análise.")) return;

        try {
            const res = await fetch(`${getApiUrl()}/api/cotacoes/public/${token}/responder`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    itens: items.map(i => ({ item_id: i.item_id, preco_unitario: i.preco_unitario })),
                    observacoes: observation
                })
            });

            if (!res.ok) throw new Error("Erro ao salvar resposta.");

            setSuccess(true);
        } catch (err) {
            alert("Erro ao enviar: " + err.message);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
    if (error) return <Container maxWidth="sm" sx={{ mt: 10 }}><Alert severity="error">{error}</Alert></Container>;
    if (success) return (
        <Container maxWidth="sm" sx={{ mt: 10, textAlign: 'center' }}>
            <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="h6">Resposta Enviada com Sucesso!</Typography>
                Obrigado. Sua cotação foi enviada para análise do responsável.
            </Alert>
        </Container>
    );

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                    {quote.titulo || 'Solicitação de Cotação'}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                    Fornecedor: <strong>{quote.fornecedor_nome}</strong>
                </Typography>
                <Divider sx={{ my: 3 }} />

                <Alert severity="info" sx={{ mb: 3 }}>
                    Por favor, preencha o valor unitário de cada item abaixo.
                </Alert>

                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell>Item</TableCell>
                                <TableCell align="right">Qtd</TableCell>
                                <TableCell align="right" width={180}>Preço Unit. (R$)</TableCell>
                                <TableCell align="right">Total (R$)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map((item, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{item.nome}</Typography>
                                        {item.unidade && <Typography variant="caption" color="textSecondary">Unid: {item.unidade}</Typography>}
                                    </TableCell>
                                    <TableCell align="right">{item.quantidade}</TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            size="small"
                                            type="number"
                                            placeholder="0.00"
                                            value={item.preco_unitario || ''}
                                            onChange={(e) => handlePriceChange(index, e.target.value)}
                                            InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                            fullWidth
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        R$ {((parseFloat(item.quantidade) || 0) * (parseFloat(item.preco_unitario) || 0)).toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 4 }}>
                    <Typography variant="h6">
                        Total Geral: R$ {calculateTotal().toFixed(2)}
                    </Typography>
                </Box>

                <TextField
                    label="Observações (Opcional)"
                    multiline
                    rows={3}
                    fullWidth
                    value={observation}
                    onChange={(e) => setObservation(e.target.value)}
                    sx={{ mb: 3 }}
                />

                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    onClick={handleSubmit}
                    sx={{ py: 1.5, fontSize: '1.1rem' }}
                >
                    Enviar Cotação
                </Button>

            </Paper>
        </Container>
    );
};

export default ResponderCotacao;
